import React from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Icon from '../../../components/AppIcon';

const DepreciationChart = ({ depreciationData, isBlurred = false }) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg shadow-dropdown p-3">
          <p className="font-medium text-text-primary">{`Year: ${label}`}</p>
          <p className="text-primary">
            {`Value: ${formatCurrency(payload?.[0]?.value)}`}
          </p>
          <p className="text-text-secondary text-sm">
            {`Age: ${new Date()?.getFullYear() - label} years`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-surface border border-border rounded-lg shadow-card">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-text-primary">Depreciation Analysis</h2>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary rounded-full"></div>
              <span className="text-sm text-text-secondary">Market Value</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="TrendingDown" size={16} className="text-text-secondary" />
              <span className="text-sm text-text-secondary">Historical Trend</span>
            </div>
          </div>
        </div>
      </div>

      <div className={`relative ${isBlurred ? 'filter blur-sm' : ''}`}>
        <div className="p-6">
          {/* Chart */}
          <div className="h-80 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={depreciationData}>
                <defs>
                  <linearGradient id="valueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="year" 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-text-secondary)"
                  fontSize={12}
                  tickFormatter={formatCurrency}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="var(--color-primary)"
                  strokeWidth={2}
                  fill="url(#valueGradient)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Calendar" size={16} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Annual Depreciation</span>
              </div>
              <div className="text-2xl font-bold text-text-primary">8.5%</div>
              <div className="text-sm text-text-secondary">Industry average: 7-12%</div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingDown" size={16} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Total Depreciation</span>
              </div>
              <div className="text-2xl font-bold text-text-primary">42%</div>
              <div className="text-sm text-text-secondary">Since new purchase</div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Target" size={16} className="text-text-secondary" />
                <span className="text-sm text-text-secondary">Residual Value</span>
              </div>
              <div className="text-2xl font-bold text-text-primary">58%</div>
              <div className="text-sm text-text-secondary">Of original MSRP</div>
            </div>
          </div>

          {/* Market Analysis */}
          <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
            <h3 className="font-semibold text-text-primary mb-3 flex items-center space-x-2">
              <Icon name="BarChart3" size={16} />
              <span>Market Analysis</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-text-secondary mb-1">Market Trend</div>
                <div className="text-text-primary">Stable depreciation curve following industry standards</div>
              </div>
              <div>
                <div className="text-text-secondary mb-1">Future Outlook</div>
                <div className="text-text-primary">Expected 6-8% annual depreciation over next 3 years</div>
              </div>
            </div>
          </div>
        </div>

        {isBlurred && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/80">
            <div className="text-center p-6">
              <Icon name="Lock" size={48} className="text-text-secondary mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-text-primary mb-2">Premium Feature</h3>
              <p className="text-text-secondary mb-4">Upgrade to view detailed depreciation analysis</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DepreciationChart;